import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Switch } from "./ui/switch";
import { Eye, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-side live presence card. Shows total + per-room breakdown of viewers
 * with a heartbeat in the last 60s. Polls every 15s. Includes a toggle to make
 * the "watching now" count visible to viewers on the Portal.
 */
export default function LivePresenceCard() {
  const [data, setData] = useState({ total: 0, rooms: [] });
  const [loading, setLoading] = useState(true);
  const [publicOn, setPublicOn] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/presence/live");
      setData(r.data || { total: 0, rooms: [] });
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15_000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    api.get("/admin/settings/presence-public")
      .then((r) => setPublicOn(!!r.data?.show_to_viewers))
      .catch(() => {});
  }, []);

  const togglePublic = async (checked) => {
    setPublicOn(checked); // optimistic
    setSavingToggle(true);
    try {
      await api.post("/admin/settings/presence-public", { show_to_viewers: checked });
      toast.success(checked ? "Viewers will now see the live count." : "Viewers will no longer see the live count.");
    } catch (e) {
      setPublicOn(!checked); // revert
      toast.error(formatApiError(e));
    } finally {
      setSavingToggle(false);
    }
  };

  return (
    <div className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-7" data-testid="live-presence-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <Eye size={17} className="text-emerald-600" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Live right now</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-heading text-3xl font-medium text-slate-900 tracking-tight" data-testid="presence-total">
                {data.total}
              </span>
              <span className="text-sm text-slate-500">watching now</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Show to viewers</span>
            <Switch checked={publicOn} disabled={savingToggle} onCheckedChange={togglePublic} data-testid="presence-public-toggle" />
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-slate-400 hover:text-slate-700 p-1.5 rounded-full"
            aria-label="Refresh"
            data-testid="presence-refresh"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className="mt-5">
        {data.rooms.length === 0 ? (
          <div className="text-sm text-slate-400">No one has the Portal open right now.</div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="presence-rooms">
            {data.rooms.map((r) => (
              <li
                key={r.room_id}
                className="rounded-2xl bg-slate-50 ring-1 ring-slate-100 px-4 py-3 flex items-center justify-between gap-3"
                data-testid={`presence-room-${r.room_id}`}
              >
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold truncate">
                    {r.room_name}
                  </div>
                  <div className="text-[11px] text-slate-400">
                    {r.room_id === "_idle" ? "Tab open, no video" : "Watching video"}
                  </div>
                </div>
                <div className="font-heading text-2xl font-medium text-slate-900 tracking-tight">{r.count}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="mt-4 text-[11px] text-slate-400">
        Counts viewers with the Portal open in the last 60 seconds. Auto-refreshes every 15s.
      </p>
    </div>
  );
}
